import { useTheme } from "@mui/material";
import { styled } from "@mui/system";
import { Link as RouterLink } from "react-router-dom";
import { trackEvent } from "src/Amplitude";
import BalanceTooltip from "src/components/wallets/header/BalanceTooltip";
import LiquidityIcon from "src/icons/portfolio/LiquidityIcon";
import StarIcon from "src/icons/portfolio/StarIcon";
import TrendingIcon from "src/icons/portfolio/TrendingIcon";

export const WatchlistHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
  fontSize: "14px",
  [theme.breakpoints.down(448)]: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
}));

export const WatchlistSubHeader = styled(RouterLink)(({ theme }) => ({
  textDecoration: "none",
  color: "inherit",
  cursor: "pointer",
  "&:hover": {
    opacity: 0.7,
  },
}));

export const WatchlistFilterButtonWrapper = styled("div")(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "flex-start",
  flex: "1 0 0",
  gap: "8px",

  [theme.breakpoints.down(448)]: {
    justifyContent: "flex-start",
  },
}));

interface WatchlistButtonProps {
  selected: boolean;
}
export const WatchlistButton = styled("div")<WatchlistButtonProps>(({ selected, theme }) => ({
  display: "flex",
  padding: "2px 4px",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  fontSize: "14px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
  userSelect: "none",

  // conditionals
  borderRadius: "8px",
  background:
    theme.palette.mode === "light"
      ? selected
        ? "#F5F3EF"
        : "#F8F9F9"
      : selected
      ? "#2B2B2B"
      : "#1A1A1A",
  border: "1px solid transparent",
  color:
    theme.palette.mode === "light"
      ? selected
        ? "#1D1A12"
        : "#000"
      : selected
      ? "#F5F5F5"
      : "#E0E0E0",

  "&:hover": {
    background:
      theme.palette.mode === "light"
        ? selected
          ? "#F5F3EF"
          : "#FCFCFB"
        : selected
        ? "#2B2B2B"
        : "#2C2C2C",
    border: `1px solid ${
      theme.palette.mode === "light"
        ? theme.palette.secondary.contrastText
        : theme.palette.primary.contrastText
    }`,
  },
}));

interface WatchlistTabsProps {
  selectedfilter: string;
  onFilterClick: (opt: string) => void;
}

const WatchlistTabs = (props: WatchlistTabsProps) => {
  const { selectedfilter, onFilterClick } = props;
  const theme = useTheme();
  const IconHighlightBox = ({
    children,
    selected,
  }: {
    children: React.ReactNode;
    selected: boolean;
  }) => {
    const hightlightStyle = {
      display: "flex",
      padding: "4px",
      justifyContent: "center",
      alignItems: "center",
      gap: "4px",
      borderRadius: "8px",
      background: selected ? (theme.palette.mode === "light" ? "#E5E1D7" : "#5e5e5e") : "",
    };

    return <div style={hightlightStyle}>{children}</div>;
  };

  const handleAmplitudeTracking = () => {
    trackEvent(`Clicked 'Your crypto' Button`, undefined, "watchlist");
  };
  return (
    <WatchlistHeader>
      <WatchlistSubHeader to="/wallets" onClick={handleAmplitudeTracking}>
        Your Crypto
      </WatchlistSubHeader>
      <WatchlistFilterButtonWrapper data-testid={`selected-tab-is-${selectedfilter}`}>
        <BalanceTooltip message="This shows your top holdings in your portfolio ">
          <WatchlistButton
            id="portfolio-tab"
            selected={selectedfilter === "portfolio"}
            onClick={() => onFilterClick("portfolio")}
            translate="no">
            <IconHighlightBox selected={selectedfilter === "portfolio"}>
              <LiquidityIcon size={12} color={theme.palette.text.primary} />
            </IconHighlightBox>
            Portfolio
          </WatchlistButton>
        </BalanceTooltip>

        <BalanceTooltip message="This shows the tokens you have watchlisted">
          <WatchlistButton
            id="watchlist-tab"
            selected={selectedfilter === "watchlist"}
            onClick={() => onFilterClick("watchlist")}
            translate="no">
            <IconHighlightBox selected={selectedfilter === "watchlist"}>
              <StarIcon size={12} color={theme.palette.text.primary} />
            </IconHighlightBox>
            Watchlist
          </WatchlistButton>
        </BalanceTooltip>

        <BalanceTooltip message="This shows the tokens that are currently trending in C&B">
          <WatchlistButton
            id="trending-tab"
            selected={selectedfilter === "trending"}
            onClick={() => onFilterClick("trending")}
            translate="no">
            <IconHighlightBox selected={selectedfilter === "trending"}>
              <TrendingIcon size={12} color={theme.palette.text.primary} />
            </IconHighlightBox>
            Trending
          </WatchlistButton>
        </BalanceTooltip>
      </WatchlistFilterButtonWrapper>
    </WatchlistHeader>
  );
};

export default WatchlistTabs;
